import { OrbitElements, isClosed } from './elements'

const TAU = Math.PI * 2
const wrap = (a: number): number => ((a % TAU) + TAU) % TAU

/**
 * Risolve M = E − e·sin E con Newton-Raphson.
 * Il punto di partenza e quello di Danby, che converge in poche iterazioni
 * anche a eccentricita alte, dove partire da E = M sarebbe lento.
 */
export function solveKepler(meanAnomaly: number, ecc: number, tol = 1e-14): number {
  if (ecc === 0) return meanAnomaly
  const M = wrap(meanAnomaly)
  let E = M + 0.85 * ecc * Math.sign(Math.sin(M) || 1)

  for (let i = 0; i < 60; i++) {
    const f = E - ecc * Math.sin(E) - M
    const fp = 1 - ecc * Math.cos(E)
    const dE = f / fp
    E -= dE
    if (Math.abs(dE) < tol) break
  }
  return E
}

/** Risolve M = e·sinh H − H. */
export function solveKeplerHyperbolic(meanAnomaly: number, ecc: number, tol = 1e-13): number {
  const M = meanAnomaly
  let H = Math.sign(M) * Math.log((2 * Math.abs(M)) / ecc + 1.8)
  if (!Number.isFinite(H)) H = M

  for (let i = 0; i < 100; i++) {
    const f = ecc * Math.sinh(H) - H - M
    const fp = ecc * Math.cosh(H) - 1
    const dH = f / fp
    H -= dH
    if (Math.abs(dH) < tol) break
  }
  return H
}

export function trueToMean(trueAnomaly: number, ecc: number): number {
  if (ecc === 0) return wrap(trueAnomaly)
  if (ecc < 1) {
    const E = 2 * Math.atan2(
      Math.sqrt(1 - ecc) * Math.sin(trueAnomaly / 2),
      Math.sqrt(1 + ecc) * Math.cos(trueAnomaly / 2),
    )
    return wrap(E - ecc * Math.sin(E))
  }
  const H = 2 * Math.atanh(
    Math.sqrt((ecc - 1) / (ecc + 1)) * Math.tan(trueAnomaly / 2),
  )
  return ecc * Math.sinh(H) - H
}

export function meanToTrue(meanAnomaly: number, ecc: number): number {
  if (ecc === 0) return wrap(meanAnomaly)
  if (ecc < 1) {
    const E = solveKepler(meanAnomaly, ecc)
    return wrap(2 * Math.atan2(
      Math.sqrt(1 + ecc) * Math.sin(E / 2),
      Math.sqrt(1 - ecc) * Math.cos(E / 2),
    ))
  }
  const H = solveKeplerHyperbolic(meanAnomaly, ecc)
  return 2 * Math.atan(
    Math.sqrt((ecc + 1) / (ecc - 1)) * Math.tanh(H / 2),
  )
}

/** Velocita angolare media, rad/s. */
export const meanMotion = (el: OrbitElements): number =>
  Math.sqrt(el.mu / Math.abs(el.sma) ** 3)

/**
 * Propagazione analitica: nessuna integrazione, quindi nessuna deriva
 * numerica per quanto lontano si vada nel tempo. E questo che rende
 * possibile il time warp a 100000×.
 */
export function propagate(el: OrbitElements, dt: number): OrbitElements {
  if (dt === 0) return el
  const M0 = trueToMean(el.trueAnomaly, el.ecc)
  const M1 = M0 + meanMotion(el) * dt
  const nu = meanToTrue(isClosed(el) ? wrap(M1) : M1, el.ecc)
  return { ...el, trueAnomaly: nu, epoch: el.epoch + dt }
}
